/* eslint-disable static-service/require-stories -- unclear if we want to
 * support importing this component anymore in the future. The story for
 * DateRangeDropdown component has a story that covers this. */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useCallback, useState} from "react";

import {
    OptionItem,
    SeparatorItem,
    SingleSelect,
} from "@khanacademy/wonder-blocks-dropdown";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";

import DateRangeModal from "./date-range-modal.tsx";
import type {
    DateRange,
    DateRangeProps,
    DateRangeValue,
    TransformValueFn,
} from "./types.ts";

const OPEN_DATE_RANGE_MODAL_OPTION_ITEM_VALUE = "open-date-range-modal";

interface Props extends DateRangeProps {
    transformValue: TransformValueFn;
}

const BaseDateRangeDropdown = ({
    dateRangeSelection,
    transformValue,
    onChange,
    onChangeCallback,
    style,
    hideCustomOption,
    hideAllTimeOption,
    renderLabel = (v: DateRangeValue) =>
        ({
            "*": t`All time`,
            today: t`Today`,
            "last-7-days": t`Last 7 days`,
            "last-30-days": t`Last 30 days`,
            custom: t`Custom range`,
        })[v],
    disabled,
    id,
    maxDate,
}: Props): React.ReactElement => {
    const [showModal, setShowModal] = useState(false);
    const [customDateRange, setCustomDateRange] = useState<
        DateRange | null | undefined
    >(
        dateRangeSelection.selectedValue === "custom"
            ? dateRangeSelection.dateRange
            : null,
    );

    const handleOptionChange = useCallback(
        (value?: any) => {
            // If the custom option is selected, don't do anything until the user confirms the date.
            if (value === OPEN_DATE_RANGE_MODAL_OPTION_ITEM_VALUE) {
                return;
            }

            const selectedValue = value as DateRangeValue;
            const dateRange = transformValue(selectedValue);

            onChange({selectedValue, dateRange});

            if (onChangeCallback) {
                onChangeCallback(selectedValue, dateRange);
            }
        },
        [onChange, onChangeCallback, transformValue],
    );

    const handleConfirmCustomDateRangeModal = useCallback(
        (dateRange: DateRange) => {
            const customDateRange = transformValue("custom", dateRange);

            setCustomDateRange(customDateRange);
            setShowModal(false);

            onChange({
                selectedValue: "custom",
                dateRange: customDateRange,
            });

            if (onChangeCallback) {
                onChangeCallback("custom", customDateRange);
            }
        },
        [onChange, onChangeCallback, transformValue],
    );

    const maybeRenderCustomRangeOption = useCallback(() => {
        const {selectedValue} = dateRangeSelection;
        const {startDate: customStartDate, endDate: customEndDate} =
            customDateRange || {}; // Use customDateRange here
        if (!customStartDate || !customEndDate || selectedValue !== "custom") {
            return null;
        }

        const startDate = customStartDate
            ? moment(customStartDate).format("MMM Do")
            : "";
        const endDate = customEndDate
            ? moment(customEndDate).format("MMM Do")
            : "";

        const text =
            startDate || endDate ? [startDate, endDate].join(" - ") : "";

        return <OptionItem label={text} value="custom" />;
    }, [dateRangeSelection, customDateRange]);

    return (
        <>
            <SingleSelect
                id={id}
                aria-label={id ? undefined : "Date range"}
                selectedValue={dateRangeSelection.selectedValue}
                onChange={handleOptionChange}
                placeholder=""
                dropdownStyle={styles.dropdownStyle}
                testId="date-range-filter"
                disabled={disabled}
                style={style}
            >
                <OptionItem
                    testId="date-range-filter-option-today"
                    key="today"
                    value="today"
                    label={renderLabel("today")}
                />
                <OptionItem
                    testId="date-range-filter-option-last-7-days"
                    key="last-7-days"
                    value="last-7-days"
                    label={renderLabel("last-7-days")}
                />
                <OptionItem
                    testId="date-range-filter-option-last-30-days"
                    key="last-30-days"
                    value="last-30-days"
                    label={renderLabel("last-30-days")}
                />
                {!hideAllTimeOption && (
                    <OptionItem
                        testId="date-range-filter-option-*"
                        key="*"
                        value="*"
                        label={renderLabel("*")}
                    />
                )}
                {maybeRenderCustomRangeOption()}
                {!hideCustomOption && <SeparatorItem />}
                {!hideCustomOption && (
                    <OptionItem
                        label={renderLabel("custom")}
                        value={OPEN_DATE_RANGE_MODAL_OPTION_ITEM_VALUE}
                        onClick={() => {
                            setShowModal(true);
                        }}
                    />
                )}
            </SingleSelect>

            <ModalLauncher
                opened={!hideCustomOption && showModal}
                onClose={() => {
                    setShowModal(false);
                }}
                modal={
                    <DateRangeModal
                        onConfirm={handleConfirmCustomDateRangeModal}
                        startDate={dateRangeSelection.dateRange?.startDate}
                        endDate={dateRangeSelection.dateRange?.endDate}
                        maxDate={maxDate}
                    />
                }
            />
        </>
    );
};

const styles = StyleSheet.create({
    dropdownStyle: {
        maxHeight: "50vh",
    },
});

export default React.memo(BaseDateRangeDropdown);
